import { Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import {
  listIntegrations,
  deleteAllIntegrations,
  regenerateKnowledgeBase,
  getBankDefaults,
  saveBankDefaults,
  type RegenerateKbResult,
  type SettingsIntegration,
  type BankStatementDefaults,
} from '../lib/settingsApi';
import { IntegrationCard } from '../components/settings/IntegrationCard';
import { UploadLimitCard } from '../components/settings/UploadLimitCard';
import { BackupCard } from '../components/settings/BackupCard';
import { WhatsAppCard } from '../components/settings/WhatsAppCard';
import { useModules } from '../hooks/useModules';
import { DailyStatusCard } from '../components/settings/DailyStatusCard';
import { AiBudgetSettings } from '../components/settings/AiBudgetSettings';
import { CompaniesSettings } from '../components/settings/CompaniesSettings';
import { BankAccountsSettings } from '../components/settings/BankAccountsSettings';
import { ExpenseCategoriesSettings } from '../components/settings/ExpenseCategoriesSettings';
import { SupplierCategoriesSettings } from '../components/settings/SupplierCategoriesSettings';
import { SubscriptionCategoriesSettings } from '../components/settings/SubscriptionCategoriesSettings';
import { LeadsPipelinesSettings } from '../components/settings/LeadsPipelinesSettings';
import { DepartmentsSettings } from '../components/settings/DepartmentsSettings';
import { ServicesSettings } from '../components/settings/ServicesSettings';
import { TaskStatusesSettings } from '../components/settings/TaskStatusesSettings';
import { TaskTemplatesSettings } from '../components/settings/TaskTemplatesSettings';
import { EmailTemplatesSettings } from '../components/settings/EmailTemplatesSettings';
import { EmailHistorySettings } from '../components/settings/EmailHistorySettings';
import { RolesPermissionsSettings } from '../components/settings/RolesPermissionsSettings';
import { TeamManagementSettings } from '../components/settings/TeamManagementSettings';
import { ModulesSettings } from '../components/settings/ModulesSettings';
import { AlertsSettings, SYSTEM_ALERTS_SUMMARY_KEY } from '../components/settings/AlertsSettings';
import { getSystemAlertsSummary } from '../lib/systemAlertsApi';
import { Select } from '../components/ui/Select';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../store/useAuthStore';
import { useI18n } from '../contexts/I18nContext';
import { useActiveTabInView } from '../hooks/useActiveTabInView';
import { OnboardingSettings } from '../components/onboarding/OnboardingSettings';

// Settings page (CLE-139 + CLE-140 + CLE-144 + CLE-153 + CLE-193). Tabbed:
//   - Integrations: integration credentials (Test Connection + encrypted Save).
//   - Expense categories: the expense-category nomenclator used by bank
//     reconciliation.
//   - Bank defaults: default credit/debit categories applied on import.
//   - Leads pipelines: lead pipeline & stage configuration (also reachable at
//     /settings/leads).
//   - Email history: ADMIN-only delivery log + open tracking (also reachable at
//     /settings/emails; moved here from its own sidebar entry).
//   - Knowledge Base: regenerate the DB -> KB markdown projections.

const SETTINGS_KEY = ['settings', 'integrations'] as const;
const BANK_DEFAULTS_KEY = ['settings', 'bank-defaults'] as const;

const ALL_TABS = [
  'onboarding',
  'alerts',
  'modules',
  'companies',
  'bank-accounts',
  'integrations',
  'ai-budget',
  'categories',
  'supplier-categories',
  'subscription-categories',
  'bank-defaults',
  'leads',
  'departments',
  'services',
  'task-statuses',
  'task-templates',
  'email-templates',
  'email-history',
  'daily-status',
  'team',
  'roles',
  'kb',
] as const;

type Tab = (typeof ALL_TABS)[number];

function isTab(value: string | null): value is Tab {
  return value !== null && (ALL_TABS as readonly string[]).includes(value);
}

export function SettingsPage({ initialTab = 'integrations' }: { initialTab?: Tab } = {}) {
  const { t } = useI18n();
  // The active tab lives in the URL (?tab=...) so it survives reloads and can
  // be shared/bookmarked. Falls back to initialTab (e.g. /settings/leads).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const requestedTab: Tab = isTab(urlTab) ? urlTab : initialTab;

  function setTab(next: Tab) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  }
  const { can } = usePermissions();
  const canManageUsers = can('settings', 'canManageUsers');
  const canManageRoles = can('settings', 'canManageRoles');
  // Fallback on canManageUsers: matrices saved before these flags existed don't
  // carry them, but a user-manager admin should still see the new tabs.
  const canEditDepartments = can('settings', 'canEditDepartments') || canManageUsers;
  const canEditServices = can('settings', 'canEditServices') || canManageUsers;
  const canEditTaskStatuses = can('settings', 'canEditTaskStatuses') || canManageUsers;
  const canEditTaskTemplates = can('settings', 'canEditTaskTemplates') || canManageUsers;
  const canEditEmailTemplates = can('settings', 'canEditEmailTemplates') || canManageUsers;
  // Multi-company: firm management (add/edit/set-default/delete).
  const canManageCompanies = can('settings', 'canManageCompanies');
  // Email history and Integrations are gated on the raw role rather than the
  // permission matrix: `/api/admin/emails` and `/api/settings/integrations*` are
  // `authorizeRole('ADMIN')`, so a matrix flag could only ever promise access
  // the API would then refuse. (There is no `can('settings', …)` flag for
  // integrations, and adding one would mean touching SYSTEM_ROLE_TEMPLATES and
  // its sync test.)
  const isAdmin = useAuthStore((s) => s.user?.role) === 'ADMIN';
  // The badge on the Alerte tab: open alerts, red while one is still unseen.
  const alertsSummary = useQuery({
    queryKey: SYSTEM_ALERTS_SUMMARY_KEY,
    queryFn: getSystemAlertsSummary,
    enabled: isAdmin,
    refetchInterval: 60_000,
  });

  const TABS: Array<{ key: Tab; labelKey: string }> = [
    // Push notifications are personal and live on the Profile page (every
    // role has one); this page holds only administrative settings.
    ...(isAdmin ? [{ key: 'alerts' as Tab, labelKey: 'settings.tab.alerts' }] : []),
    ...(isAdmin ? [{ key: 'modules' as Tab, labelKey: 'settings.tab.modules' }] : []),
    // Setări → Onboarding (spec import istoric, pasul 7): per-firm setup and
    // financial-history import; its steps API needs settings.canManageCompanies.
    ...(canManageCompanies
      ? [{ key: 'onboarding' as Tab, labelKey: 'settings.tab.onboarding' }]
      : []),
    ...(canManageCompanies
      ? [{ key: 'companies' as Tab, labelKey: 'settings.tab.companies' }]
      : []),
    ...(canManageCompanies
      ? [{ key: 'bank-accounts' as Tab, labelKey: 'settings.tab.bankAccounts' }]
      : []),
    ...(isAdmin ? [{ key: 'integrations' as Tab, labelKey: 'settings.tab.integrations' }] : []),
    { key: 'ai-budget', labelKey: 'settings.tab.aiBudget' },
    { key: 'categories', labelKey: 'settings.tab.categories' },
    { key: 'supplier-categories', labelKey: 'settings.tab.supplierCategories' },
    { key: 'subscription-categories', labelKey: 'settings.tab.subscriptionCategories' },
    { key: 'bank-defaults', labelKey: 'settings.tab.bankDefaults' },
    { key: 'leads', labelKey: 'settings.tab.leads' },
    ...(canEditDepartments
      ? [{ key: 'departments' as Tab, labelKey: 'settings.tab.departments' }]
      : []),
    ...(canEditServices ? [{ key: 'services' as Tab, labelKey: 'settings.tab.services' }] : []),
    ...(canEditTaskStatuses
      ? [{ key: 'task-statuses' as Tab, labelKey: 'settings.tab.taskStatuses' }]
      : []),
    ...(canEditTaskTemplates
      ? [{ key: 'task-templates' as Tab, labelKey: 'settings.tab.taskTemplates' }]
      : []),
    ...(canEditEmailTemplates
      ? [{ key: 'email-templates' as Tab, labelKey: 'settings.tab.emailTemplates' }]
      : []),
    ...(isAdmin ? [{ key: 'email-history' as Tab, labelKey: 'settings.tab.emailHistory' }] : []),
    ...(isAdmin ? [{ key: 'daily-status' as Tab, labelKey: 'settings.tab.dailyStatus' }] : []),
    ...(canManageUsers ? [{ key: 'team' as Tab, labelKey: 'settings.tab.team' }] : []),
    ...(canManageRoles ? [{ key: 'roles' as Tab, labelKey: 'settings.tab.roles' }] : []),
    { key: 'kb', labelKey: 'settings.tab.kb' },
  ];

  // `initialTab` defaults to 'integrations', which is now ADMIN-only — without
  // this fallback a non-admin opening /settings would land on a tab that isn't
  // in the strip and render nothing at all.
  const tab: Tab = TABS.some((item) => item.key === requestedTab)
    ? requestedTab
    : (TABS[0]?.key ?? requestedTab);
  const tabsRef = useActiveTabInView<HTMLDivElement>(tab);
  const alertsBadge =
    alertsSummary.data && alertsSummary.data.open > 0 ? alertsSummary.data.open : 0;

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="text-sm text-gray-500">{t('settings.subtitle')}</p>
      </div>

      {/* Phones: one searchable "Secțiune" picker. Twenty-odd tabs in a
          sideways strip meant swiping blind, with the active one off screen. */}
      <div className="mt-6 sm:hidden">
        <Select
          label={t('settings.section')}
          value={tab}
          onChange={(v) => v && setTab(v as Tab)}
          options={TABS.map((item) => ({
            value: item.key,
            label:
              item.key === 'alerts' && alertsBadge > 0
                ? `${t(item.labelKey)} (${alertsBadge})`
                : t(item.labelKey),
          }))}
          searchable
          className="w-full"
        />
      </div>

      <div
        ref={tabsRef}
        className="relative mt-6 hidden gap-1 overflow-x-auto border-b border-gray-200 sm:flex"
      >
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            aria-current={tab === item.key ? 'page' : undefined}
            className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium ${
              tab === item.key
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t(item.labelKey)}
            {item.key === 'alerts' && alertsSummary.data && alertsSummary.data.open > 0 && (
              <span
                className={`ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold text-white ${
                  alertsSummary.data.unacknowledged > 0 ? 'bg-red-500' : 'bg-amber-500'
                }`}
              >
                {alertsSummary.data.open}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === 'alerts' && isAdmin && <AlertsSettings />}
        {tab === 'modules' && isAdmin && <ModulesSettings />}
        {tab === 'onboarding' && canManageCompanies && <OnboardingSettings />}
        {tab === 'companies' && canManageCompanies && <CompaniesSettings />}
        {tab === 'bank-accounts' && canManageCompanies && <BankAccountsSettings />}
        {tab === 'integrations' && isAdmin && <IntegrationsTab />}
        {tab === 'ai-budget' && <AiBudgetSettings />}
        {tab === 'categories' && <ExpenseCategoriesSettings />}
        {tab === 'supplier-categories' && <SupplierCategoriesSettings />}
        {tab === 'subscription-categories' && <SubscriptionCategoriesSettings />}
        {tab === 'bank-defaults' && <BankDefaultsTab />}
        {tab === 'leads' && <LeadsPipelinesSettings />}
        {tab === 'departments' && canEditDepartments && <DepartmentsSettings />}
        {tab === 'services' && canEditServices && <ServicesSettings />}
        {tab === 'task-statuses' && canEditTaskStatuses && <TaskStatusesSettings />}
        {tab === 'task-templates' && canEditTaskTemplates && <TaskTemplatesSettings />}
        {tab === 'email-templates' && canEditEmailTemplates && <EmailTemplatesSettings />}
        {tab === 'email-history' && isAdmin && <EmailHistorySettings />}
        {tab === 'daily-status' && isAdmin && <DailyStatusCard />}
        {tab === 'team' && canManageUsers && <TeamManagementSettings />}
        {tab === 'roles' && canManageRoles && <RolesPermissionsSettings />}
        {tab === 'kb' && <KnowledgeBaseTab />}
      </div>
    </div>
  );
}

function BankDefaultsTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: BANK_DEFAULTS_KEY, queryFn: getBankDefaults });

  const [draft, setDraft] = useState<BankStatementDefaults | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const defaults = draft ?? query.data?.defaults ?? null;

  function update(patch: Partial<BankStatementDefaults>) {
    if (!defaults) return;
    setDraft({ ...defaults, ...patch });
    setStatus('idle');
  }

  async function handleSave() {
    if (!defaults) return;
    setStatus('saving');
    setError(null);
    try {
      const saved = await saveBankDefaults(defaults);
      qc.setQueryData(BANK_DEFAULTS_KEY, (prev) => (prev ? { ...prev, defaults: saved } : prev));
      setDraft(saved);
      setStatus('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
      setStatus('error');
    }
  }

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-gray-500">{t('settings.bank.intro')}</p>

      {query.isLoading && (
        <p className="mt-6 text-sm text-gray-500">{t('settings.bank.loading')}</p>
      )}
      {query.isError && (
        <p className="mt-6 text-sm text-red-600">
          {t('settings.bank.loadError')}
          {query.error instanceof Error ? `: ${query.error.message}` : ''}.
        </p>
      )}

      {query.data && defaults && (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="text-base font-semibold text-gray-900">{t('settings.bank.heading')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('settings.bank.headingHint')}</p>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Select
              label={t('settings.bank.credit')}
              value={defaults.creditCategoryCode}
              onChange={(v) => update({ creditCategoryCode: v })}
              options={query.data.categories.map((c) => ({ value: c.code, label: c.name }))}
            />

            <Select
              label={t('settings.bank.debit')}
              value={defaults.debitCategoryCode}
              onChange={(v) => update({ debitCategoryCode: v })}
              options={query.data.categories.map((c) => ({ value: c.code, label: c.name }))}
            />
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={status === 'saving'}
            className="mt-4 inline-flex items-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === 'saving' ? t('settings.bank.saving') : t('settings.bank.save')}
          </button>

          {status === 'done' && (
            <p className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-600">
              {t('settings.bank.saved')}
            </p>
          )}
          {status === 'error' && (
            <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
              {t('settings.bank.saveError')}
              {error ? `: ${error}` : ''}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function KnowledgeBaseTab() {
  const { t } = useI18n();
  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<RegenerateKbResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleRegenerate() {
    setStatus('running');
    setError(null);
    setResult(null);
    try {
      const res = await regenerateKnowledgeBase();
      setResult(res);
      setStatus('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
      setStatus('error');
    }
  }

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-gray-500">{t('settings.kb.intro')}</p>

      {/* The KB page is no longer in the sidebar — Settings is its entry point. */}
      <Link
        to="/knowledge-base"
        className="mt-4 inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        {t('settings.kb.open')}
      </Link>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="text-base font-semibold text-gray-900">{t('settings.kb.heading')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('settings.kb.headingHint')}</p>

        <button
          type="button"
          onClick={handleRegenerate}
          disabled={status === 'running'}
          className="mt-4 inline-flex items-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'running' ? t('settings.kb.regenerating') : t('settings.kb.regenerate')}
        </button>

        {status === 'done' && result && (
          <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-400">
            <p className="font-medium">{t('settings.kb.success')}</p>
            <ul className="mt-1 list-inside list-disc">
              <li>{t('settings.kb.result.clients', { count: result.clients })}</li>
              <li>{t('settings.kb.result.suppliers', { count: result.suppliers })}</li>
              <li>{t('settings.kb.result.projects', { count: result.projectsWithFinancials })}</li>
              <li>{t('settings.kb.result.firmCosts', { count: result.firmCostRows })}</li>
              <li>{t('settings.kb.result.reports', { count: result.monthlyReports })}</li>
              {result.leadsIndex && <li>{t('settings.kb.result.leads')}</li>}
              <li>{t('settings.kb.result.bankMonths', { count: result.bankMonths })}</li>
              <li>{t('settings.kb.result.tasks', { count: result.projectsWithTasks })}</li>
              <li>{t('settings.kb.result.reindexed', { count: result.reindexedChunks })}</li>
              <li>
                {result.driveMirrored === null
                  ? t('settings.kb.result.driveOff')
                  : t('settings.kb.result.driveMirrored', { count: result.driveMirrored })}
              </li>
              <li>
                {t('settings.kb.result.aiSummaries', {
                  state: result.aiEnabled
                    ? t('settings.kb.ai.enabled')
                    : t('settings.kb.ai.disabled'),
                })}
              </li>
            </ul>
          </div>
        )}
        {status === 'error' && (
          <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
            {t('settings.kb.error')}
            {error ? `: ${error}` : ''}.
          </p>
        )}
      </div>
    </div>
  );
}

function IntegrationsTab() {
  const { t } = useI18n();
  const { isOn } = useModules();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [wiping, setWiping] = useState(false);
  const [wipeError, setWipeError] = useState<string | null>(null);
  // Global + user scopes. The per-user ones (Google Calendar, Gmail) store their
  // app credentials in the same encrypted table but had no screen at all, so
  // they could be neither inspected nor deleted.
  //
  // Fetching both scopes explicitly rather than dropping the filter: an
  // unfiltered list would also include SmartBill (`scope: 'company'`), whose
  // scope-less read aliases the default firm — a second, ambiguous SmartBill
  // card duplicating the one on CompanyDetailPage.
  const query = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async () => {
      const [global, user] = await Promise.all([
        listIntegrations('global'),
        listIntegrations('user'),
      ]);
      return [...global, ...user];
    },
  });

  function handleChanged(updated: SettingsIntegration) {
    qc.setQueryData<SettingsIntegration[]>(SETTINGS_KEY, (prev) =>
      prev ? prev.map((i) => (i.key === updated.key ? updated : i)) : prev,
    );
  }

  async function handleWipeAll() {
    if (
      !(await confirm({
        title: t('settings.integrations.wipe.title'),
        message: t('settings.integrations.wipe.message'),
        confirmLabel: t('settings.integrations.wipe.confirm'),
        danger: true,
      }))
    )
      return;
    setWiping(true);
    setWipeError(null);
    try {
      await deleteAllIntegrations();
      await qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    } catch (e) {
      setWipeError(e instanceof Error ? e.message : t('settings.unknownError'));
    } finally {
      setWiping(false);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-gray-500">{t('settings.integrations.intro')}</p>
        <button
          type="button"
          onClick={handleWipeAll}
          disabled={wiping}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
        >
          <Trash2 className="h-4 w-4" />
          {t('settings.integrations.wipe.button')}
        </button>
      </div>
      {wipeError && <p className="mt-2 text-sm text-red-600">{wipeError}</p>}

      {query.isLoading && (
        <p className="mt-6 text-sm text-gray-500">{t('settings.integrations.loading')}</p>
      )}
      {query.isError && (
        <p className="mt-6 text-sm text-red-600">
          {t('settings.integrations.loadError')}
          {query.error instanceof Error ? `: ${query.error.message}` : ''}.
        </p>
      )}

      {query.data && (
        <div className="mt-6 flex flex-col gap-4">
          {query.data
            // WhatsApp is an opt-in module: no card where it is not granted.
            .filter((integration) => integration.key !== 'whatsapp' || isOn('whatsapp'))
            .map((integration) => (
              <Fragment key={integration.key}>
                <IntegrationCard integration={integration} onChanged={handleChanged} />
                {/* Upload limit and backup both concern Drive, so they sit right under it. */}
                {integration.key === 'googleDrive' && (
                  <>
                    <UploadLimitCard />
                    <BackupCard />
                  </>
                )}
                {/* Session (QR) and the group → client links, under the address/token. */}
                {integration.key === 'whatsapp' && <WhatsAppCard />}
              </Fragment>
            ))}
        </div>
      )}
    </>
  );
}
